/**
 * Web MIDI hook — connects to the browser's Web MIDI API, tracks
 * connected inputs/outputs, dispatches parsed messages to a subscriber
 * queue, and exposes a `sendLed` helper for hardware feedback.
 *
 * Chrome / Edge / Opera support Web MIDI out of the box. Firefox and Safari
 * do not (yet). We degrade gracefully: `supported: false` and the UI can
 * point the user at Chrome / Edge.
 *
 * This module is 100% client-side; no backend involvement. Mappings live
 * in localStorage.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { findPresetForDevice } from "@/lib/midi/presets";

const LS_MAPPINGS = "nd_midi_mappings_v1";
const LS_MODEL = "nd_midi_model_v1";

/** Parse a raw MIDI byte triplet into a friendly event.
 *  Byte 0: status (upper nibble = type, lower = channel-1)
 *  Byte 1: note or CC number
 *  Byte 2: velocity or CC value
 */
function parseMidi(data) {
  if (!data || data.length < 2) return null;
  const status = data[0];
  const type = status & 0xf0;
  const channel = (status & 0x0f) + 1; // 1-indexed for readability
  const b1 = data[1] || 0;
  const b2 = data[2] || 0;
  if (type === 0x90) {
    // Note On (0-velocity == Note Off)
    return { kind: b2 === 0 ? "noteoff" : "noteon", channel, note: b1, velocity: b2, sig: `note:${channel}:${b1}` };
  }
  if (type === 0x80) return { kind: "noteoff", channel, note: b1, velocity: b2, sig: `note:${channel}:${b1}` };
  if (type === 0xb0) return { kind: "cc", channel, cc: b1, value: b2, sig: `cc:${channel}:${b1}` };
  if (type === 0xe0) {
    const val14 = (b2 << 7) | b1;
    return { kind: "pb", channel, value: val14, sig: `pb:${channel}:0` };
  }
  return null;
}

function loadMappings() {
  try {
    const raw = localStorage.getItem(LS_MAPPINGS);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveMappings(m) {
  try { localStorage.setItem(LS_MAPPINGS, JSON.stringify(m)); } catch { /* ignore quota */ }
}

export function useMidi() {
  const [supported, setSupported] = useState(true);
  const [access, setAccess] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | ready | denied | unsupported
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [detectedModel, setDetectedModel] = useState(null);
  const [mappings, setMappings] = useState(loadMappings);
  const [learnTarget, setLearnTarget] = useState(null); // {deck, action} being learned
  const [lastEvent, setLastEvent] = useState(null);
  // Most-recent event that had NO matching binding. Studio surfaces this via
  // the "Bind last signal" pill so users can zero-click Learn without visiting
  // Settings. Cleared as soon as a binding is added or the pill is dismissed.
  const [lastUnmapped, setLastUnmapped] = useState(null);
  const dismissUnmapped = useCallback(() => setLastUnmapped(null), []);
  const subscribersRef = useRef(new Set());

  // Subscribe to translated action dispatches. Returns unsubscribe fn.
  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => subscribersRef.current.delete(cb);
  }, []);

  const refreshPorts = useCallback((mAccess) => {
    if (!mAccess) return;
    const ins = Array.from(mAccess.inputs.values());
    const outs = Array.from(mAccess.outputs.values());
    setInputs(ins);
    setOutputs(outs);
    // Auto-detect the first recognised Pioneer preset. Try the port name
    // first, then fall back to `manufacturer` — on macOS Pioneer gear often
    // reports as generic "USB MIDI Interface" but `manufacturer === "Pioneer"`.
    for (const inp of ins) {
      const p = findPresetForDevice(inp.name) || findPresetForDevice(inp.manufacturer);
      if (p) { setDetectedModel(p); break; }
    }
  }, []);

  const requestAccess = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.requestMIDIAccess) {
      setSupported(false);
      setStatus("unsupported");
      return;
    }
    setStatus("requesting");
    try {
      const mAccess = await navigator.requestMIDIAccess({ sysex: false });
      setAccess(mAccess);
      setStatus("ready");
      refreshPorts(mAccess);
      mAccess.onstatechange = () => refreshPorts(mAccess);
    } catch {
      setStatus("denied");
    }
  }, [refreshPorts]);

  // Wire up every input's onmidimessage once we have access
  useEffect(() => {
    if (!access) return;
    const handler = (msg) => {
      const evt = parseMidi(msg.data);
      if (!evt) return;
      setLastEvent(evt);
      // If we're in Learn mode, bind the last touched signature to the target.
      if (learnTarget && (evt.kind === "noteon" || evt.kind === "cc")) {
        setMappings((prev) => {
          const next = { ...prev, [evt.sig]: learnTarget };
          saveMappings(next);
          return next;
        });
        setLearnTarget(null);
        return;
      }
      // Otherwise dispatch through mappings. Preset mapping is a fallback
      // (only if the user hasn't remapped this signature manually).
      const custom = mappings[evt.sig];
      const preset = detectedModel && detectedModel.mapping[evt.sig];
      const target = custom || preset;
      if (!target) {
        // Zero-click Learn: remember this signature so the Studio can offer
        // a one-tap "Bind last signal" action. Only track true user gestures
        // (note-on / cc) — pitch-bend spam from jog wheels shouldn't hijack the pill.
        if (evt.kind === "noteon" || evt.kind === "cc") setLastUnmapped(evt);
        return;
      }
      const payload = { ...target, event: evt };
      subscribersRef.current.forEach((cb) => {
        try { cb(payload); } catch { /* swallow subscriber errors */ }
      });
    };
    for (const inp of Array.from(access.inputs.values())) inp.onmidimessage = handler;
    return () => {
      for (const inp of Array.from(access.inputs.values())) inp.onmidimessage = null;
    };
  }, [access, mappings, detectedModel, learnTarget]);

  const setModel = useCallback((preset) => {
    setDetectedModel(preset);
    if (preset?.id) try { localStorage.setItem(LS_MODEL, preset.id); } catch {}
  }, []);

  const clearMappings = useCallback(() => {
    setMappings({});
    saveMappings({});
  }, []);

  const removeMapping = useCallback((sig) => {
    setMappings((prev) => {
      const next = { ...prev };
      delete next[sig];
      saveMappings(next);
      return next;
    });
  }, []);

  // Send a Note On to hardware to light an LED. `velocity=0` turns it off.
  const sendLed = useCallback((channel, note, velocity = 127) => {
    if (!access) return;
    const status = 0x90 | ((channel - 1) & 0x0f);
    for (const out of Array.from(access.outputs.values())) {
      try { out.send([status, note & 0x7f, velocity & 0x7f]); } catch { /* ignore */ }
    }
  }, [access]);

  // Explicit binding for the last unmapped signal — called by the zero-click
  // Learn pill in Studio. Wraps the same LS-persistent map as MIDI Learn.
  const bindSignature = useCallback((sig, target) => {
    if (!sig || !target?.action) return;
    setMappings(prev => {
      const next = { ...prev, [sig]: target };
      saveMappings(next);
      return next;
    });
    setLastUnmapped(null);
  }, []);

  return {
    supported, status, inputs, outputs, detectedModel, mappings,
    learnTarget, lastEvent, lastUnmapped,
    requestAccess, setModel, setLearnTarget, clearMappings, removeMapping,
    subscribe, sendLed, bindSignature, dismissUnmapped,
  };
}
